import {
    StyledBox,
    StyledContainer,
    StyledInnerContainer,
    StyledDetailsContainer,
    StyledSmallPosterContainer,
    StyledSmallPoster,
    StyledDataWrapper,
} from "./StyledComponents";
import { BlurredBackgroundImage } from "./BackgroundBlur";
import { useMediaQuery } from "@kaltura/mediaspace-shared-utils";
import { useTheme } from "@kaltura/mediaspace-shared-styled";
import Details from "./details/Details";
import { KmsTypeMediaSchedulingData, KmsTypeComponent } from "@kaltura/mediaspace-shared-types";
import NotSupportedModal from "./modal/NotSupportedModal";
import { ReactNode, useContext } from "react";
import { ConfigContext } from "@kaltura/mediaspace-shared-data-kms-config";

export interface LobbyProps {
    videoUrl?: string;
    thumbnailUrl: string;
    statusLabel?: string;
    // Show the status label even if there's no title
    alwaysShowStatusLabel?: boolean;
    title: string;
    schedulingData?: KmsTypeMediaSchedulingData;
    actions?: KmsTypeComponent[];
    kmeNotSupported?: boolean;
    /**
     * extra lobby data component - loaded after the initial load. presenters etc.
     */
    data?: ReactNode;
}

/**
 * lobby component
 * being used in all hero components, and converting screen
 */
const Lobby = (props: LobbyProps) => {
    const {
        videoUrl,
        thumbnailUrl,
        statusLabel,
        alwaysShowStatusLabel,
        title,
        schedulingData,
        actions = [],
        kmeNotSupported = false,
        data,
    } = props;

    const staticClassBase = "kms-ds-media-page-lobby";
    const theme = useTheme();
    const isSm = useMediaQuery(theme.breakpoints.down("md"));
    // lobby content width is different in case of kaf
    const configContext = useContext(ConfigContext);
    const isKaf = configContext.application?.isKAF;

    return (
        <StyledBox className={staticClassBase}>
            <BlurredBackgroundImage
                imageUrl={thumbnailUrl}
                blurSize={100}
                className={`${staticClassBase}-blurred-background`}
            />
            <StyledContainer className={staticClassBase + "-data"} isKaf={isKaf}>
                <StyledInnerContainer>
                    <StyledDetailsContainer>
                        <Details
                            schedulingData={schedulingData}
                            statusLabel={statusLabel}
                            alwaysShowStatusLabel={alwaysShowStatusLabel}
                            title={title}
                            actions={actions}
                        />
                        {!isSm && data && <StyledDataWrapper>{data}</StyledDataWrapper>}
                    </StyledDetailsContainer>

                    {!isSm && (
                        <StyledSmallPosterContainer className={staticClassBase + "-poster"}>
                            {videoUrl && (
                                <video
                                    className={staticClassBase + "-poster-video"}
                                    autoPlay
                                    loop
                                    muted
                                    playsInline
                                    poster={thumbnailUrl}
                                    aria-hidden={true}
                                >
                                    <source src={videoUrl} type="video/mp4" />
                                </video>
                            )}
                            {!videoUrl && (
                                <StyledSmallPoster
                                    className={staticClassBase + "-poster-image"}
                                    aria-hidden={true}
                                    style={{
                                        backgroundImage: `url(${thumbnailUrl})`,
                                    }}
                                />
                            )}
                        </StyledSmallPosterContainer>
                    )}

                    {kmeNotSupported && <NotSupportedModal />}
                </StyledInnerContainer>
            </StyledContainer>
        </StyledBox>
    );
};

export default Lobby;
